import { withStyleNonce } from '@/lib/csp-nonce';
import { useState, type ReactNode } from 'react';
import { ArrowLeftIcon, CopyIcon, PencilSimpleIcon, ProhibitIcon } from '@phosphor-icons/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui-kit/button';
import { CampaignStatusBadge } from '../components/campaign-status-badge/campaign-status-badge';
import { CampaignTypeBadge } from '../components/campaign-type-badge/campaign-type-badge';
import { useCancelCampaign, useGetCampaignDetails } from '../hooks/use-campaigns';
import { useDuplicateCampaignRest } from '../hooks/use-campaigns-rest';
import type { CampaignItem } from '../types/campaigns.types';
import { formatTimezoneLabel } from '../constants/timezones.constants';
import {
  buildCloneCampaignName,
  formatCampaignDate,
  mapCampaignItemToCampaign,
} from '../utils/campaigns-mapper.utils';
import { canEditCampaign } from '../utils/can-edit-campaign.utils';
import { getNextCampaignSendDate } from '../utils/next-send-date.utils';

const DetailField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="text-sm text-muted-foreground">{value || '—'}</div>
  </div>
);

const MetricCard = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="rounded-lg border bg-card p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

const getEmailPreviewHtml = (campaign: CampaignItem): string => {
  const html = campaign.CampaignMediaDetails?.find((media) =>
    media.Templet?.trim()
  )?.Templet?.trim();
  return html ?? '';
};

const getEmailSubject = (campaign: CampaignItem): string => {
  const subject = campaign.CampaignMediaDetails?.find((media) =>
    media.Subject?.trim()
  )?.Subject?.trim();
  return subject ?? '—';
};

export const CampaignDetailsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { campaignId = '' } = useParams();
  const { campaign, isLoading, isError, error } = useGetCampaignDetails(campaignId);
  const { mutateAsync: cloneCampaign, isPending: isCloning } = useDuplicateCampaignRest();
  const { mutateAsync: cancelCampaign, isPending: isCancelling } = useCancelCampaign();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-xl border bg-card p-6">
        <p className="text-sm text-muted-foreground">Loading campaign details...</p>
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-xl border bg-card p-6">
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'Campaign not found'}
        </p>
        <Button type="button" variant="outline" onClick={() => navigate('/campaigns')}>
          Back to Campaigns
        </Button>
      </div>
    );
  }

  const view = mapCampaignItemToCampaign(campaign);
  const showEdit = canEditCampaign(view.status);
  const previewHtml = getEmailPreviewHtml(campaign);
  const tags = (campaign.Tags ?? []).filter(Boolean);
  const isBusy = isCloning || isCancelling;
  const canCancelCampaign =
    (view.type === 'Scheduled' || view.type === 'Recurring') && view.status === 'Published';
  const showNextSendDate = view.type === 'Recurring' || view.type === 'Scheduled';
  const nextSendDate = showNextSendDate
    ? getNextCampaignSendDate({
        type: view.type,
        frequency: campaign.ScheduledorRecurringStatus,
        startDate: campaign.StartDate,
        scheduleTime: campaign.ScheduleTime,
        endDate: campaign.EndDate,
      })
    : null;

  const handleCloneCampaign = async () => {
    if (isBusy) return;

    const cloneName = buildCloneCampaignName(view.name);

    try {
      await cloneCampaign({ campaignId: view.id, input: { name: cloneName } });
      toast({
        variant: 'success',
        title: 'Campaign cloned',
        description: `"${cloneName}" was created successfully.`,
      });
      navigate('/campaigns');
    } catch (cloneError) {
      toast({
        variant: 'destructive',
        title: 'Failed to clone campaign',
        description: cloneError instanceof Error ? cloneError.message : 'Please try again.',
      });
    }
  };

  const handleCancelCampaign = async () => {
    if (isBusy || !canCancelCampaign) return;

    try {
      await cancelCampaign(view.id);
      toast({
        variant: 'success',
        title: 'Campaign cancelled',
        description: `"${view.name}" was set back to Draft.`,
      });
      setIsCancelConfirmOpen(false);
    } catch (cancelError) {
      toast({
        variant: 'destructive',
        title: 'Failed to cancel campaign',
        description: cancelError instanceof Error ? cancelError.message : 'Please try again.',
      });
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          <Link to="/campaigns" className="font-medium text-primary-600">
            Campaigns
          </Link>{' '}
          / {view.name}
        </p>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="mt-0.5 h-8 w-8 shrink-0"
              aria-label="Back to campaigns"
              onClick={() => navigate('/campaigns')}
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-3xl font-semibold tracking-tight text-foreground">
                  {view.name}
                </h1>
                <CampaignStatusBadge status={view.status} />
              </div>
              <p className="text-sm text-muted-foreground">{view.subject || 'No email subject'}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {showEdit ? (
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={() => navigate(`/campaigns/edit/${view.id}`)}
              >
                <PencilSimpleIcon className="mr-2 h-4 w-4" />
                Edit
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              disabled={isBusy}
              onClick={() => void handleCloneCampaign()}
            >
              <CopyIcon className="mr-2 h-4 w-4" />
              {isCloning ? 'Cloning...' : 'Clone'}
            </Button>
            {canCancelCampaign ? (
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={() => setIsCancelConfirmOpen(true)}
              >
                <ProhibitIcon className="mr-2 h-4 w-4" />
                {isCancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Sent" value={Number(campaign.TimesSent ?? 0).toLocaleString()} />
        <MetricCard label="Opened" value="—" hint="Metrics unavailable" />
        <MetricCard label="Clicked" value="—" hint="Metrics unavailable" />
        <MetricCard
          label="Recipients"
          value={Number(campaign.TotalRecipent ?? 0).toLocaleString()}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h2 className="text-base font-semibold text-foreground">Campaign details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <DetailField label="Type" value={<CampaignTypeBadge type={view.type} />} />
            <DetailField label="Status" value={<CampaignStatusBadge status={view.status} />} />
            <DetailField label="Date" value={view.scheduledOrSentAt ?? '—'} />
            <DetailField label="Subject" value={getEmailSubject(campaign)} />
            <DetailField label="Segmentation" value={campaign.SegmentationType?.trim() || '—'} />
            <DetailField
              label="Schedule time"
              value={formatCampaignDate(campaign.ScheduleTime) ?? '—'}
            />
            <DetailField
              label="Time zone"
              value={
                campaign.TimeZone?.trim() ? formatTimezoneLabel(campaign.TimeZone.trim()) : '—'
              }
            />
            <DetailField label="Start date" value={formatCampaignDate(campaign.StartDate) ?? '—'} />
            <DetailField label="End date" value={formatCampaignDate(campaign.EndDate) ?? '—'} />
            <DetailField
              label="Published"
              value={formatCampaignDate(campaign.DatePublished) ?? '—'}
            />
            <DetailField label="Created" value={formatCampaignDate(campaign.CreatedDate) ?? '—'} />
            <DetailField
              label="Last updated"
              value={formatCampaignDate(campaign.LastUpdatedDate) ?? '—'}
            />
          </div>
          {tags.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h2 className="text-base font-semibold text-foreground">Audience</h2>
          <DetailField label="Target audience" value={campaign.TargetAudience?.trim() || '—'} />
          <DetailField
            label="Target all contacts"
            value={campaign.IsCampaignTargetAll ? 'Yes' : 'No'}
          />
          <DetailField
            label="Recurring or scheduled"
            value={campaign.IsRecurringOrScheduled ? 'Yes' : 'No'}
          />
          {showNextSendDate ? (
            <DetailField
              label="Next campaign send date"
              value={nextSendDate ? formatCampaignDate(nextSendDate.toISOString()) : '—'}
            />
          ) : null}
          {campaign.CronExpression?.trim() ? (
            <DetailField label="Cron expression" value={campaign.CronExpression.trim()} />
          ) : null}
        </section>
      </div>

      <section className="space-y-4 rounded-xl border bg-card p-5">
        <h2 className="text-base font-semibold text-foreground">Email preview</h2>
        {previewHtml ? (
          <iframe
            title={`${view.name} email preview`}
            srcDoc={withStyleNonce(previewHtml)}
            className="h-[32rem] w-full rounded-md border bg-white"
            sandbox=""
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            No email content available for this campaign.
          </p>
        )}
      </section>

      <ConfirmationModal
        open={isCancelConfirmOpen}
        onOpenChange={setIsCancelConfirmOpen}
        title="Cancel Campaign"
        description={`Are you sure you want to cancel "${view.name}"? Its status will be set to Draft and it will no longer send.`}
        confirmText={isCancelling ? 'CANCELLING...' : 'CANCEL CAMPAIGN'}
        cancelText="KEEP"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        onConfirm={handleCancelCampaign}
      />
    </div>
  );
};
